import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { sntTranslate } from "src/app/support/translate";
import { Observable } from "rxjs";
import { UserNotify } from "src/app/support/notify-decorator";
import { environment } from "src/environments/environment";
import { map, switchMap } from "rxjs/operators";
import {
  SoapTemplate,
  SoapTemplateLight,
  SoapTemplateCreate,
  SoapTemplateUpdate
} from "src/app/backend/models/soap-template";

@Injectable()
export class SoapTemplateBackend {
  constructor(private http: HttpClient) {}

  load(id: string): Observable<SoapTemplate> {
    return this.http
      .get<SoapTemplate>(`${environment.snoots.api.healthcare}/templates/${id}`)
      .pipe(map(extendDoc));
  }

  loadLight(id: string): Observable<SoapTemplateLight> {
    return this.http.get<SoapTemplateLight>(
      `${environment.snoots.api.healthcare}/templates/${id}`,
      {
        params: { light: true }
      }
    );
  }

  @UserNotify({
    successMessage: marker("Modello SOAP creato"),
    errorMessage: marker("Impossibile creare il modello SOAP")
  })
  create(payload: SoapTemplateCreate): Observable<SoapTemplate> {
    return this.http
      .post<SoapTemplate>(
        `${environment.snoots.api.healthcare}/templates`,
        payload
      )
      .pipe(map(extendDoc));
  }

  list(request: { published?: boolean }): Observable<SoapTemplate[]> {
    return this.http
      .get<SoapTemplate[]>(`${environment.snoots.api.healthcare}/templates`, {
        params: {
          ...request
        }
      })
      .pipe(map((list) => list.map(extendDoc)));
  }

  listAll(): Observable<SoapTemplate[]> {
    return this.list({});
  }

  listAllPublished(): Observable<SoapTemplate[]> {
    return this.list({
      published: true
    });
  }

  searchPublished(query: string): Observable<SoapTemplate[]> {
    return this.listAllPublished().pipe(
      map((templates) =>
        templates.filter((template) =>
          query
            ? template.name.toLowerCase().includes(query.toLowerCase())
            : true
        )
      )
    );
  }

  update(id: string, payload: SoapTemplateUpdate): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/templates/${id}`,
      payload
    );
  }

  @UserNotify({
    successMessage: marker("Modello SOAP clonato"),
    errorMessage: marker("Impossibile clonare il modello SOAP")
  })
  clone(id: string): Observable<SoapTemplate> {
    return this.http.put<SoapTemplate>(
      `${environment.snoots.api.healthcare}/templates/${id}/clone`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Modello SOAP duplicato"),
    errorMessage: marker("Impossibile duplicare il modello SOAP")
  })
  duplicate(id: string): Observable<SoapTemplate> {
    return this.load(id).pipe(
      switchMap((template) => {
        const payload: SoapTemplateCreate = {
          name: template.name + sntTranslate(marker(" copia")),
          staff: template.staff?.map((s) => ({
            id: s.id,
            number: s.number,
            firstName: s.firstName,
            lastName: s.lastName
          })),
          note: template.note,
          detail: { ...template.detail }
        };

        return this.http
          .post<SoapTemplate>(
            `${environment.snoots.api.healthcare}/templates`,
            payload
          )
          .pipe(map(extendDoc));
      })
    );
  }

  @UserNotify({
    successMessage: marker("Modello SOAP pubblicato"),
    errorMessage: marker("Impossibile pubblicare il modello SOAP")
  })
  publish(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.healthcare}/templates/${id}/publish`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Modello SOAP eliminato"),
    errorMessage: marker("Impossibile eliminare il modello SOAP")
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.healthcare}/templates/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Modello SOAP abilitato"),
    errorMessage: marker("Impossibile ablitare il modello SOAP")
  })
  enable(id: string): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/templates/${id}`,
      {
        enabled: true
      }
    );
  }

  @UserNotify({
    successMessage: marker("Modello SOAP disablitato"),
    errorMessage: marker("Impossibile disabilitare il modello SOAP")
  })
  disable(id: string): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/templates/${id}`,
      {
        enabled: false
      }
    );
  }
}

function extendDoc(soapTemplate: SoapTemplate): SoapTemplate {
  return {
    ...soapTemplate
  };
}
